import jsPDF from 'jspdf';

const money = n => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

function guessKind(fileName = '', contentType = '') {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (contentType.includes('pdf') || ext === 'pdf') return 'pdf';
  if (contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  return 'other';
}

// ── Draws the main ECRF-style report page(s) ────────────────────────────────
function drawReport(doc, r) {
  const W = 210, H = 297;
  const marginX = 12;

  // Header bar
  doc.setFillColor(219, 234, 254);
  doc.rect(0, 0, W, 22, 'F');
  doc.setDrawColor(26, 82, 118);
  doc.setLineWidth(0.6);
  doc.rect(0, 0, W, 22, 'D');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 82, 118);
  doc.text('TSE WORKSPACE — TRAVEL ADVANCE REQUEST', W / 2, 10, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Expense Claim & Requisition Report', W / 2, 16, { align: 'center' });

  let y = 30;
  const rowH = 7;

  const field = (label, value, x, w) => {
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.rect(x, y, w, rowH, 'D');
    doc.setFontSize(7.2);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 2, y + 3);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(17, 24, 39);
    doc.text(String(value ?? '—'), x + 2, y + 6);
  };

  // Row 1: Request No / Period / Date
  const colW = (W - marginX * 2) / 3;
  field('REQUEST NO', r.requestId, marginX, colW);
  field('DATE OF REQUEST', fmtDate(r.createdAt), marginX + colW, colW);
  field('STATUS', r.status, marginX + colW * 2, colW);
  y += rowH;

  // Row 2: Employee / Dept / Designation
  field('NAME OF REQUISITIONER', r.employeeName || r.requestedBy?.userName, marginX, colW);
  field('DEPARTMENT', r.department, marginX + colW, colW);
  field('DESIGNATION', r.designation, marginX + colW * 2, colW);
  y += rowH;

  // Row 3: Purpose (full width)
  field('SUBJECT / PURPOSE OF TRAVEL', r.purposeOfTravel, marginX, W - marginX * 2);
  y += rowH;

  // Row 4: Location / Mode / Days
  field('TRAVEL LOCATION', r.travelLocation, marginX, colW);
  field('MODE OF TRAVEL', r.modeOfTravel, marginX + colW, colW);
  field('TOTAL DAYS', r.totalDays, marginX + colW * 2, colW);
  y += rowH;

  // Row 5: Dates
  field('TRAVEL START DATE', fmtDate(r.travelStartDate), marginX, colW);
  field('TRAVEL END DATE', fmtDate(r.travelEndDate), marginX + colW, colW);
  field('DATE REQUIRED', fmtDate(r.dateRequired), marginX + colW * 2, colW);
  y += rowH + 4;

  // Amount table
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 82, 118);
  doc.text('ADVANCE / AMOUNT DETAILS', marginX, y);
  y += 3;

  const amtHeaders = ['Estimated Total (₹)', 'Advance Requested (₹)', 'Approved Amount (₹)', 'Released Amount (₹)'];
  const amtValues = [money(r.estimatedTotalExpense), money(r.advanceAmountRequested), r.approvedAmount != null ? money(r.approvedAmount) : '—', money(r.advanceReleasedAmount)];
  const amtColW = (W - marginX * 2) / 4;
  amtHeaders.forEach((h, i) => {
    const x = marginX + i * amtColW;
    doc.setFillColor(248, 250, 252);
    doc.rect(x, y, amtColW, 6, 'F');
    doc.setDrawColor(180, 180, 180);
    doc.rect(x, y, amtColW, 6, 'D');
    doc.setFontSize(6.6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(107, 114, 128);
    doc.text(h, x + 1.5, y + 4);
  });
  y += 6;
  amtValues.forEach((v, i) => {
    const x = marginX + i * amtColW;
    doc.rect(x, y, amtColW, 8, 'D');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text(v, x + 1.5, y + 5.5);
  });
  y += 8 + 6;

  // Approval trail
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 82, 118);
  doc.text('APPROVAL TRAIL', marginX, y);
  y += 3;

  const apHeaders = ['Role', 'Approver', 'Action', 'Date', 'Remarks'];
  const apColW = [22, 40, 22, 26, W - marginX * 2 - (22 + 40 + 22 + 26)];
  let x = marginX;
  apHeaders.forEach((h, i) => {
    doc.setFillColor(248, 250, 252);
    doc.rect(x, y, apColW[i], 6, 'F');
    doc.setDrawColor(180, 180, 180);
    doc.rect(x, y, apColW[i], 6, 'D');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(107, 114, 128);
    doc.text(h, x + 1.5, y + 4);
    x += apColW[i];
  });
  y += 6;

  const history = r.approvalHistory && r.approvalHistory.length ? r.approvalHistory : [];
  if (history.length === 0) {
    doc.rect(marginX, y, W - marginX * 2, 7, 'D');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('No approval action recorded yet', marginX + 2, y + 4.5);
    y += 7;
  } else {
    history.forEach((h) => {
      x = marginX;
      const rowVals = [h.role || '—', h.approverId?.userName || '—', h.action || '—', fmtDate(h.actionAt), h.remarks || '—'];
      rowVals.forEach((v, i) => {
        doc.rect(x, y, apColW[i], 7, 'D');
        doc.setFontSize(7.4);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(17, 24, 39);
        const text = doc.splitTextToSize(String(v), apColW[i] - 3);
        doc.text(text[0] || '', x + 1.5, y + 4.5);
        x += apColW[i];
      });
      y += 7;
    });
  }
  y += 8;

  // Attachment note
  const attachmentList = Array.isArray(r.attachments) && r.attachments.length
    ? r.attachments
    : (r.attachment?.fileName ? [r.attachment] : []);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(55, 65, 81);
  doc.text('Attachments: ', marginX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(
    attachmentList.length
      ? `${attachmentList.length} image${attachmentList.length !== 1 ? 's' : ''} (see following page${attachmentList.length !== 1 ? 's' : ''})`
      : 'None on file',
    marginX + 26, y
  );
  y += 12;

  // Signatures
  const sigY = Math.min(y + 20, H - 30);
  const sigW = (W - marginX * 2) / 3;
  ['Signature of Requisitioner', 'Approving Manager', 'Approving Finance'].forEach((label, i) => {
    const sx = marginX + i * sigW;
    doc.setDrawColor(100, 100, 100);
    doc.line(sx, sigY, sx + sigW - 10, sigY);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(label, sx, sigY + 4);
  });

  // Footer note
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(156, 163, 175);
  doc.text(
    'Generated from TSE Workspace. Valid receipts/bills must be attached for final settlement; all reimbursement is subject to company policy.',
    marginX, H - 10
  );
  doc.text(`Report generated on ${new Date().toLocaleString('en-IN')}`, marginX, H - 6);
}

// ── Adds every already-uploaded image attachment as its own full page ──────
// Restricted to images at the point of upload (frontend accepts image/* only),
// so this embeds each one directly with jsPDF — one image per page, sized to
// fill the page. Any legacy non-image attachment (from older records) still
// gets a reference/link page instead of failing the whole report.
async function appendAttachment(doc, attachment) {
  if (!attachment?.fileUrl) return;

  const kind = guessKind(attachment.fileName);

  if (kind === 'image') {
    try {
      const res = await fetch(attachment.fileUrl);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      doc.addPage();
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Attachment — ${attachment.fileName}`, 12, 12);
      const format = attachment.fileName.toLowerCase().endsWith('.png') ? 'PNG' : 'JPEG';
      // Fill the page as large as possible while preserving aspect ratio.
      const W = 210, H = 297;
      const marginX = 10, top = 18, bottom = 10;
      const maxW = W - marginX * 2;
      const maxH = H - top - bottom;
      let imgW = maxW, imgH = maxH;
      try {
        const dims = doc.getImageProperties(dataUrl);
        const ratio = dims.width / dims.height;
        if (maxW / maxH > ratio) {
          imgH = maxH;
          imgW = maxH * ratio;
        } else {
          imgW = maxW;
          imgH = maxW / ratio;
        }
      } catch {
        // Fall back to filling the max box if dimensions can't be read.
      }
      const x = marginX + (maxW - imgW) / 2;
      const y = top + (maxH - imgH) / 2;
      doc.addImage(dataUrl, format, x, y, imgW, imgH, undefined, 'FAST');
      return;
    } catch {
      // fall through to the reference page below
    }
  }

  doc.addPage();
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Attachment', 12, 20);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(55, 65, 81);
  doc.text(`File: ${attachment.fileName}`, 12, 30);
  doc.setTextColor(26, 82, 118);
  doc.textWithLink('Open attachment', 12, 38, { url: attachment.fileUrl });
}

export async function generateAdvanceReportPDF(r, getAttachmentUrl, filename) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  drawReport(doc, r);

  const attachmentList = Array.isArray(r.attachments) && r.attachments.length
    ? r.attachments
    : (r.attachment?.s3Key ? [r.attachment] : []);

  if (attachmentList.length && typeof getAttachmentUrl === 'function') {
    for (const att of attachmentList) {
      if (!att?.s3Key) continue;
      try {
        const signedUrl = await getAttachmentUrl(att.s3Key);
        await appendAttachment(doc, { ...att, fileUrl: signedUrl });
      } catch {
        // If we can't get a fresh link for this one, skip it rather than
        // failing the whole report.
      }
    }
  }

  const fname = filename || `TSE_Advance_Report_${(r.requestId || 'request').replace(/\//g, '-')}.pdf`;
  doc.save(fname);
}
